import { createAsyncThunk } from '@reduxjs/toolkit';

import taxInfoSchema from '@/components/PaymentElement/schema';
import ResponseError from '@/errors/responseError';
import paymentModel, { PAYMENT_STATUS, PRICE_NAMES } from '@/models/payment';
import { storage } from '@/utils/browser';

import { fetchBillingInfo } from '../profile/thunks';
import { STEPS } from './constants';

const USER_COUNTRY_HEADER_KEY = 'x-user-country';

export const fetchPrices = createAsyncThunk(
	'checkout/fetchPrices',
	async ({ country, currency, filterActive }, { rejectWithValue }) => {
		try {
			return await paymentModel.getPrices({
				country,
				currency,
				filterActive
			});
		} catch (error) {
			if (error instanceof ResponseError) {
				return rejectWithValue({ status: error.status, data: error.data });
			}

			throw error;
		}
	}
);

export function saveCheckoutData({ priceId, step, paymentHandlerId }) {
	storage.save('checkout', { priceId, step, paymentHandlerId });
}

export function getUserCurrency(user) {
	if (user?.paymentInfo?.currency) {
		return user.paymentInfo.currency;
	}
}

export const getPrices = createAsyncThunk(
	'checkout/getPrices',
	async (payload, { rejectWithValue, getState }) => {
		const { country, allowSelectedCurrency } = payload;
		const { user, currency: selectedCurrency } = getState().profile;
		const userCurrency = getUserCurrency(user);

		try {
			const { prices, headers = {} } = await paymentModel.getPrices({
				country: country || user?.taxInformation?.country,
				currency: allowSelectedCurrency
					? selectedCurrency || userCurrency
					: userCurrency,
				filterActive: true
			});

			const userCountry =
				country ||
				user?.billingData?.country ||
				headers[USER_COUNTRY_HEADER_KEY]?.toLowerCase() ||
				null;

			return { prices, userCountry };
		} catch (error) {
			if (error instanceof Response) {
				return rejectWithValue({ status: error.status, data: error.data });
			}

			throw error;
		}
	}
);

export const initCheckout = createAsyncThunk(
	'checkout/initCheckout',
	async (_, { rejectWithValue, getState, dispatch }) => {
		try {
			const state = getState();

			/*
				When loading checkout without passing through billing page (accessing directly from url)
				we have to know if user has a payment method attached or not, for doing that we
				fetch billing data within this thunk only if it wasn't fetched before.
			*/
			if (!state.profile.fetchBillingInfo.completed) {
				await dispatch(fetchBillingInfo()).unwrap();
			}

			const profileState = state.profile;
			const { taxInformation } = profileState.user;
			let step = STEPS.BILLING;

			const checkoutData = storage.get('checkout') || {};

			const userCurrency = getUserCurrency(profileState.user);

			// TODO: set saved payment method and currency subscription
			const { prices, headers = {} } = await paymentModel.getPrices({
				country: taxInformation?.country,
				currency: profileState.currency || userCurrency,
				filterActive: false
			});

			let clientSecret = null;

			if (checkoutData.paymentHandlerId) {
				const response = await paymentModel.getByHandlerId(
					checkoutData.paymentHandlerId
				);

				/*
					If payment is already paid/canceled, we do not want to retry it.
					In checkout component if `paymentHandlerId` is present and `clientSecret` is `null` it will redirect to billing automatically.
				*/
				if (
					[PAYMENT_STATUS.PAID, PAYMENT_STATUS.CANCELED].includes(
						response.status
					)
				) {
					clientSecret = null;
				} else {
					clientSecret = response.handlerClientSecret;
				}
			}

			const activePrices = prices.filter(price => price.active);
			const userCountry =
				headers[USER_COUNTRY_HEADER_KEY]?.toLowerCase() || null;

			let validPriceId = activePrices.find(
				price => price.handlerId === checkoutData.priceId
			);

			if (!validPriceId?.handlerId) {
				const validInactivePrice = prices.find(
					price => price.handlerId === checkoutData.priceId
				);

				validPriceId = activePrices.find(
					price => price.name === validInactivePrice?.name
				);
			}

			if (validPriceId) {
				step = taxInfoSchema({ sync: true }).isValidSync(taxInformation)
					? STEPS.PAYMENT
					: STEPS.BILLING;
			}

			const priceId =
				validPriceId?.handlerId ||
				activePrices.find(price => price.name === PRICE_NAMES.TRIAL)
					?.handlerId;

			return {
				priceId,
				paymentHandlerId: checkoutData.paymentHandlerId,
				clientSecret,
				userCountry,
				activePrices,
				step
			};
		} catch (error) {
			if (error instanceof Response) {
				return rejectWithValue({ status: error.status, data: error.data });
			}

			throw error;
		}
	}
);
